import { useEffect, useRef, useState } from 'react';
import { debounce } from '../utils/debounce';

export function useAdInitialization() {
  const initialized = useRef(false);
  const adRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasSize, setHasSize] = useState(false);
  const uniqueId = useRef(`ad-${Math.random().toString(36).substring(2, 11)}`);

  useEffect(() => {
    const currentRef = adRef.current;
    if (!currentRef) return;
    
    // Add unique ID to prevent duplicate initialization
    currentRef.id = uniqueId.current;

    // Check if element has size
    const checkSize = () => {
      if (!currentRef) return;
      const rect = currentRef.getBoundingClientRect();
      setHasSize(rect.width > 0 && rect.height > 0);
    };

    // Debounced ad initialization
    const initializeAd = debounce(() => {
      if (!initialized.current && isVisible && hasSize && currentRef) {
        try {
          // Ensure the container has proper dimensions before initializing
          if (currentRef.offsetWidth > 0 && currentRef.offsetHeight > 0) {
            // Check if this ad has already been initialized
            if (!currentRef.hasAttribute('data-ad-initialized')) {
              (window.adsbygoogle = window.adsbygoogle || []).push({});
              currentRef.setAttribute('data-ad-initialized', 'true');
              initialized.current = true;
            }
          }
        } catch (error) {
          console.error('AdSense initialization error:', error);
        }
      }
    }, 300);

    // Intersection Observer to check visibility
    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        setIsVisible(entries[0].isIntersecting);
      },
      { threshold: 0.1 }
    );

    // Resize Observer with error handling
    const resizeObserver = new ResizeObserver(() => {
      checkSize();
      if (hasSize) {
        initializeAd();
      }
    });

    // Initial size check
    checkSize();
    
    // Start observing
    intersectionObserver.observe(currentRef);
    resizeObserver.observe(currentRef);
    
    // Initialize after a delay to ensure DOM is fully rendered
    setTimeout(() => {
      checkSize();
      if (isVisible && hasSize) {
        initializeAd();
      }
    }, 1000);

    return () => {
      initializeAd.cancel();
      intersectionObserver.disconnect();
      resizeObserver.disconnect();
      initialized.current = false;
      
      // Clean up the ad when component unmounts
      if (currentRef) {
        currentRef.removeAttribute('data-ad-initialized');
      }
    };
  }, [hasSize, isVisible]);

  return { adRef, initialized };
}